/**
 * AI 채팅 두뇌
 *
 * 하는 일: 사용자의 말을 Gemini 에게 넘기고, Gemini 가 "이 SP를 불러줘" 라고 하면 실제로 불러서
 * 결과를 다시 Gemini 에게 돌려준다. 답이 나올 때까지 이 왕복을 반복한다.
 *
 * 안전 규칙 (중요):
 *   - 조회(_S_) SP 는 바로 실행한다.
 *   - 저장/수정/삭제(_T_) SP 는 절대 그냥 실행하지 않는다. 대신 "이런 값으로 저장할게요" 하고 멈춘다(status: 'confirm').
 *     화면에서 사용자가 [실행] 을 누르면 resolveWrite(session, true) 로 이어서 진행한다.
 *
 * 왜 세션 객체를 밖에서 들고 있게 했나:
 *   확인 버튼을 기다리는 동안 대화가 중간에 멈춰야 하는데, 그 동안의 대화 기록(contents)을 어딘가는 들고 있어야 한다.
 *   화면(React state)이 들고 있게 하면 채팅창을 닫았다 열어도 이어서 대화할 수 있다.
 */

import { generate } from './geminiClient';
import { searchProcedures, findProcedure, describeProcedure } from './spCatalog';
import { buildCapabilityHint, findCapability, searchCapabilities, describeCapability, buildSpParams } from './capabilities';
import { searchManual, readManual, buildManualHint } from './manualIndex';
import { runProcedure, previewProcedureCall, SpCallError } from './spRunner';
import { buildScreenToolDeclarations, describeScreenContext, findScreenAction, readScreenState } from './screenTools';
import { preloadTarget, attachTarget, targetKeyOf } from './targetInfo';
import { getUserInfo } from '../../utils/tokenManager';
import { isDemoMode } from '../../demo/demoMode';

/** 한 번의 질문에 SP를 몇 번까지 부를 수 있는지 (무한 왕복 방지) */
const MAX_STEPS = 8;

const PARAM_SCHEMA = {
  type: 'array',
  description: 'SP 파라미터 목록. 이름은 반드시 @업체번호 처럼 @ 로 시작하는 정확한 이름이어야 한다.',
  items: {
    type: 'object',
    properties: {
      name: { type: 'string', description: '파라미터 이름 (예: @상담번호)' },
      value: { type: 'string', description: '값. 숫자도 문자열로 적으면 된다.' },
    },
    required: ['name', 'value'],
  },
};

const BASE_TOOLS = [
  {
    functionDeclarations: [
      {
        name: 'search_capabilities',
        description:
          '이 ERP 에서 할 수 있는 일(기능)을 키워드로 찾는다. 어떤 요청이든 이걸 먼저 쓴다. '
          + '찾은 기능은 run_capability 로 실행하면 되고, SP 이름이나 파라미터를 직접 알 필요가 없다.',
        parameters: {
          type: 'object',
          properties: {
            keyword: { type: 'string', description: '사용자가 한 말에서 뚝 때낸 말 (예: 미수, 명단 인원 추가, 가격 재고, 상담 기록)' },
          },
          required: ['keyword'],
        },
      },
      {
        name: 'run_capability',
        description:
          'search_capabilities 로 찾은 기능을 실행한다. SP 이름·파라미터는 코드가 레시피대로 채우므로 '
          + '사람 말로 된 값만 넘기면 된다. 저장/수정 기능은 실행 전에 사용자 확인을 받는다.',
        parameters: {
          type: 'object',
          properties: {
            id: { type: 'string', description: 'search_capabilities 가 알려준 기능 id' },
            values: {
              type: 'object',
              description: "기능이 요구하는 값들. 예: { \"시작일\": \"2026-08-01\", \"종료일\": \"2026-08-31\" }",
            },
            summary: { type: 'string', description: '저장/수정일 때 확인 카드에 보여줄 한 줄 설명' },
          },
          required: ['id'],
        },
      },
      {
        name: 'search_manual',
        description:
          '화면 사용법·절차를 매뉴얼에서 찾는다. "어떻게 해요", "어디서 하나요", "이게 뭔가요" 같은 '
          + '사용법 질문에는 데이터를 조회하기 전에 이걸 먼저 쓴다. 기억으로 절차를 지어내지 말 것.',
        parameters: {
          type: 'object',
          properties: {
            질문: { type: 'string', description: '사용자가 궁금해하는 것. 사용자 말 그대로 넣어도 되고 핵심 낱말만 넣어도 된다. (예: 재고구분 요금제 차이, 견적서 보내기, 입금 처리)' },
          },
          required: ['질문'],
        },
      },
      {
        name: 'read_manual',
        description:
          'search_manual 로 찾은 장을 통째로 읽는다. 발췌만으로 절차를 정확히 설명하기 어려울 때 쓴다. '
          + '장id 를 생략하면 그 매뉴얼의 목차를 준다.',
        parameters: {
          type: 'object',
          properties: {
            매뉴얼id: { type: 'string', description: 'search_manual 이 알려준 매뉴얼id' },
            장id: { type: 'string', description: 'search_manual 이 알려준 장id (예: c3). 생략하면 목차' },
          },
          required: ['매뉴얼id'],
        },
      },
      {
        name: 'search_procedures',
        description:
          '⚠ search_capabilities 에 없는 일일 때만 쓰는 보조 수단. 키워드로 SP 를 직접 찾아 이름과 파라미터를 알려준다. '
          + '이 길로 가면 파라미터를 직접 조립해야 하고 설명에 없는 값은 알 수 없어 틀리기 쉽다.',
        parameters: {
          type: 'object',
          properties: {
            keyword: { type: 'string', description: '찾을 키워드 (예: 미수, 상담리스트, 예약현황, 수납)' },
            kind: { type: 'string', enum: ['S', 'T'], description: 'S=조회, T=저장/수정/삭제. 생략하면 둘 다.' },
          },
          required: ['keyword'],
        },
      },
      {
        name: 'run_query',
        description: '조회 SP(_S_)를 실행해서 데이터를 가져온다. 데이터를 바꾸지 않으므로 바로 실행된다.',
        parameters: {
          type: 'object',
          properties: {
            procedureName: { type: 'string', description: '정확한 SP 이름 (예: DBO.TRP_S_미수현황)' },
            params: PARAM_SCHEMA,
          },
          required: ['procedureName'],
        },
      },
      {
        name: 'run_write',
        description:
          '데이터를 저장/수정/삭제하는 SP(_T_)를 실행한다. 실제 실행 전에 사용자에게 확인을 받는다. 사용자가 거절하면 실행되지 않는다.',
        parameters: {
          type: 'object',
          properties: {
            procedureName: { type: 'string', description: '정확한 SP 이름 (예: DBO.TRP_T_상담기록정보)' },
            params: PARAM_SCHEMA,
            summary: {
              type: 'string',
              description: '사용자에게 보여줄 한 줄 설명. 예: "상담 12345 에 상담기록을 추가합니다."',
            },
          },
          required: ['procedureName', 'summary'],
        },
      },
    ],
  },
];

/**
 * 이번 턴에 쓸 도구 목록.
 * 기본 도구(SP 검색/조회/저장)에 지금 보고 있는 화면이 등록한 전용 도구를 얹는다.
 * 화면을 옮기면 목록이 달라지므로 매 턴 새로 만든다.
 */
function buildTools() {
  const screenDeclarations = buildScreenToolDeclarations();
  if (!screenDeclarations.length) return BASE_TOOLS;
  return [{ functionDeclarations: [...BASE_TOOLS[0].functionDeclarations, ...screenDeclarations] }];
}

/** 오늘 날짜 YYYY-MM-DD */
function todayIso() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * 도구 사용법. 이건 서버가 아니라 여기가 들고 있다 —
 * 위 BASE_TOOLS 와 짝이 맞아야 하는 설명이라, 도구를 고칠 때 같은 파일에서 같이 고쳐야 어긋나지 않는다.
 * (역할·범위·안전 규칙은 반대로 서버가 들고 있다. buildContext 주석 참고.)
 */
const TOOL_GUIDE = `[데이터를 다루는 방법]
이 ERP의 모든 데이터는 SQL Server 저장 프로시저(SP)로 오갑니다. 당신은 아래 도구로 SP를 직접 부를 수 있습니다.
1. 먼저 search_capabilities 로 "이 요청이 어떤 기능인지" 를 찾고, run_capability 로 실행합니다.
   이 길로 가면 SP 이름과 파라미터는 코드가 레시피대로 채우므로 틀릴 일이 없습니다.
2. 지금 화면에 전용 도구가 있고 그걸로 되는 일이라면 그 도구를 써도 됩니다. (같은 것을 가리킵니다)
3. 기능 목록에 없는 일일 때만 search_procedures 로 SP 를 직접 찾아 run_query / run_write 를 씁니다.
   이건 보조 수단입니다. 파라미터 이름을 추측해서 부르지 마세요.
4. 업체번호/유저번호/처리자 같은 값은 로그인 정보에서 자동으로 채워집니다. 지정하지 마세요.
5. 날짜는 YYYY-MM-DD 형식으로 넣습니다. "이번 달", "지난주" 같은 말은 오늘 날짜를 기준으로 직접 계산하세요.
6. 조회 결과가 비었으면 조건(기간·구분값)을 바꿔 한 번 더 시도해보고, 그래도 없으면 없다고 솔직히 말하세요.

[사용법을 묻는 질문]
"어떻게 하나요", "어디서 하나요", "이건 뭔가요" 처럼 데이터가 아니라 화면 쓰는 법을 묻는 질문에는
search_manual 로 매뉴얼에서 해당 장을 찾습니다.
7. 발췌만으로 절차를 정확히 쓸 수 없으면 read_manual 로 그 장을 통째로 읽습니다.
8. 답 끝에 근거가 된 장의 링크를 한 줄 붙입니다. 예: [가격/재고 매뉴얼 · 03 재고구분과 요금제](/manuals/product-price-stock?c=c3)
9. 사용법을 설명한 뒤, 그 일을 도우미가 대신 해 줄 수 있는 기능이 있으면 한 줄로 제안합니다.`;

/**
 * 이번 턴의 "상황" 을 모은다.
 *
 * ⚠ 여기서 규칙(역할·대답 범위·안전 수칙)을 만들지 않는다. 그 문장들은 Edge Function 이 들고 있다.
 *   브라우저가 규칙을 조립해 보내면, 개발자도구에서 그 부분만 지우고 요청을 다시 보내는 것만으로
 *   회사 Gemini 키가 개인 챗봇이 된다. 앱 토큰이 번들에 들어 있어서 그걸로는 막지 못한다.
 *   그래서 규칙은 서버가 소유하고, 여기서는 서버가 알 수 없는 값(화면·로그인·기능 목록)만 넘긴다.
 *
 *   이 값들도 결국 브라우저에서 가는 것이라 서버는 이걸 "참고 데이터" 로만 취급한다.
 *   여기에 지시문을 넣어도 규칙을 이기지 못한다.
 */
function buildContext({ route } = {}) {
  const { companyNo, userNo, userName } = getUserInfo() || {};
  return {
    today: todayIso(),
    user: { name: userName || '', userNo: userNo || '', companyNo: companyNo || '' },
    route: route || '',
    screen: describeScreenContext() || '',
    demo: isDemoMode(),
    toolGuide: TOOL_GUIDE,
    // 매뉴얼 목록은 기능 목록과 같은 자리에 실어 보낸다. 서버가 받는 칸을 늘리지 않으려는 것 —
    // 이 힌트가 없으면 도우미는 매뉴얼이 있다는 사실 자체를 모른다.
    capabilityHint: [buildCapabilityHint(), buildManualHint()].filter(Boolean).join('\n\n'),
  };
}

/** 새 대화 세션 */
export function createSession() {
  return {
    contents: [],
    queue: [], // 아직 처리하지 못한 함수호출
    responses: [], // 이번 턴에 모은 함수 실행 결과 (개수가 맞아야 API 가 받아준다)
    pendingWrite: null, // 사용자 확인을 기다리는 쓰기 작업
  };
}

const toFunctionResponse = (call, payload) => ({
  functionResponse: {
    name: call.name,
    ...(call.id ? { id: call.id } : {}),
    response: payload,
  },
});

/**
 * 화면 액션의 확인 카드 내용.
 * WriteConfirmCard 가 { sp, params:[{name,value}] } 형태를 그리므로 같은 모양으로 맞춘다.
 */
function buildScreenPreview(action, args) {
  const custom = typeof action.preview === 'function' ? action.preview(args) : null;
  const source = custom && typeof custom === 'object' ? custom : args;
  return {
    sp: action.label || `화면 동작: ${action.name}`,
    kind: 'T',
    desc: action.description || '',
    params: Object.entries(source)
      .filter(([key, value]) => key !== 'summary' && value !== undefined && value !== null && value !== '')
      .map(([key, value]) => ({
        name: key,
        value: Array.isArray(value) ? value.join(', ') : String(value),
        auto: false,
      })),
  };
}

/**
 * 확인 카드에 "무엇에다 저장하는지" 를 붙인다.
 *
 * 카드에는 @상품번호 111366 처럼 번호만 뜬다. 번호를 외우는 사람은 없으니, 그대로 두면 사용자가
 * 검증할 수 있는 건 "저장한다"는 사실뿐이고 대상이 맞는지는 확인할 방법이 없다.
 * 이름은 AI 에게 묻지 않는다 — 지어내면 틀린 이름으로 안심시키는 꼴이라 없느니만 못하다.
 *
 * 화면 도구는 화면 state 에서(화면이 이미 안다), SP 는 상품 목록에서 찾는다.
 */
function screenPreviewWithTarget(action, args) {
  return attachTarget(buildScreenPreview(action, args), readScreenState());
}

/**
 * 확인 카드를 만들기 전에 대상 이름을 받아둔다.
 * 확인 창은 값을 고칠 때마다 rebuild() 를 동기로 부르므로 그때는 물어볼 수 없다. 여기서 데워둔다.
 * (창에서 번호를 바꾸면 창이 스스로 resolveTarget 으로 다시 받아온다.)
 */
async function preloadTargetFor(preview) {
  await preloadTarget(targetKeyOf(preview, readScreenState()));
}

/** 화면 액션을 실제로 수행한다. 던지면 오류를 AI 에게 그대로 돌려준다. */
async function runScreenAction(action, args) {
  try {
    const result = await action.run(args);
    return result === undefined ? { 결과: '실행했습니다.' } : result;
  } catch (error) {
    return { 오류: error?.message || String(error) };
  }
}

/**
 * run_capability 호출을 "실제로 무엇을 할지" 로 푸례준다.
 * 기능이 SP 를 가리키면 레시피대로 파라미터를 만들고, 화면 함수를 가리키면 그 함수를 찾는다.
 * 실행 자체는 exec() 에 싸둔다 — 쓰기면 사용자 승인 뒤에 불려야 하기 때문이다.
 */
function resolveCapabilityCall(args) {
  const cap = findCapability(args?.id);
  if (!cap) {
    return { error: `'${args?.id}' 는 없는 기능입니다. search_capabilities 로 다시 찾으세요.` };
  }
  const values = args?.values && typeof args.values === 'object' ? args.values : {};

  // 빠진 필수값은 실행 전에 되묻는다 (틀린 값으로 서버까지 가지 않게)
  const missing = (cap.inputs || [])
    .filter((i) => i.required && String(values[i.key] ?? '').trim() === '')
    .map((i) => i.key);
  if (missing.length) {
    return { error: `${cap.name} 을(를) 하려면 ${missing.join(', ')} 이(가) 필요합니다. 사용자에게 물어보세요.` };
  }

  if (cap.via.type === 'screen') {
    const action = findScreenAction(cap.via.tool);
    if (!action) {
      return { error: `${cap.name} 은(는) ${cap.screen || '해당'} 화면에서만 됩니다. 사용자에게 그 화면으로 이동해 달라고 안내하세요.` };
    }
    const screenArgs = { ...values, summary: args?.summary || cap.name };
    return {
      cap,
      write: !!action.write,
      preview: () => screenPreviewWithTarget(action, screenArgs),
      exec: () => runScreenAction(action, screenArgs),
    };
  }

  const params = buildSpParams(cap, values);
  const write = cap.kind === 'write';
  return {
    cap,
    write,
    // attachTarget 은 캐시에서 동기로 읽는다. 데우는 건 확인 카드를 만들기 직전에 한 번 한다.
    preview: () => attachTarget(previewProcedureCall(cap.via.procedureName, params)),
    exec: async () => {
      try {
        const result = await runProcedure(cap.via.procedureName, params, { allowWrite: write });
        return write ? { 결과: '실행했습니다.', ...result } : result;
      } catch (error) {
        return { 오류: error?.message || String(error) };
      }
    },
  };
}

/** 조회/검색 도구 실행 */
async function executeReadCall(call) {
  const args = call.args || {};
  if (call.name === 'search_capabilities') {
    const results = searchCapabilities(args.keyword);
    return {
      찾은개수: results.length,
      기능: results,
      ...(results.length
        ? {}
        : { 안내: '기능 목록에서 못 찾았습니다. 다른 말로 한 번 더 찾아보고, 그래도 없으면 search_procedures 로 SP 를 직접 찾으세요.' }),
    };
  }
  if (call.name === 'search_manual') {
    try {
      return await searchManual(args.질문);
    } catch (error) {
      return { 오류: error?.message || '매뉴얼을 찾지 못했습니다.', 안내: '매뉴얼을 읽을 수 없다고 사용자에게 알리고, 아는 범위에서 답하되 확실하지 않다고 밝히세요.' };
    }
  }
  if (call.name === 'read_manual') {
    try {
      return await readManual(args.매뉴얼id, args.장id);
    } catch (error) {
      return { 오류: error?.message || '매뉴얼을 읽지 못했습니다.' };
    }
  }
  if (call.name === 'search_procedures') {
    const results = searchProcedures(args.keyword, { kind: args.kind });
    return {
      찾은개수: results.length,
      결과: results,
      ...(results.length ? {} : { 안내: '검색 결과가 없습니다. 다른 키워드로 다시 찾아보세요.' }),
    };
  }
  if (call.name === 'run_query') {
    const entry = findProcedure(args.procedureName);
    if (entry && entry.kind === 'T') {
      return { 오류: `${entry.sp} 는 데이터를 바꾸는 SP 입니다. run_write 로 호출하세요.` };
    }
    try {
      return await runProcedure(args.procedureName, args.params);
    } catch (error) {
      return {
        오류: error?.message || String(error),
        ...(error instanceof SpCallError && error.detail ? error.detail : {}),
        ...(entry ? { 참고: describeProcedure(entry) } : {}),
      };
    }
  }
  return { 오류: `알 수 없는 도구: ${call.name}` };
}

/**
 * 확인 창에서 사용자가 고칠 수 있는 값들을 만든다.
 *
 * AI 가 뽑은 값이 늘 맞지는 않는다(날짜를 잘못 잡거나, 이름을 잘못 알아듣거나).
 * 그때 취소하고 처음부터 다시 말하게 하는 대신, 그 자리에서 고쳐 실행할 수 있게 한다.
 * 고칠 수 있는 것은 "사람 말로 된 값" 뿐이고, SP 파라미터 조립은 여전히 코드가 한다.
 */
function fieldsFromInputs(inputs, values) {
  const given = values && typeof values === 'object' ? values : {};
  return (inputs || []).map((i) => {
    const raw = given[i.key];
    return {
      key: i.key,
      value: Array.isArray(raw) ? raw.join(', ') : raw === undefined || raw === null ? '' : String(raw),
      desc: i.desc || '',
      required: !!i.required,
      isList: Array.isArray(raw),
    };
  });
}

/** 확인 창에서 돌려받은 문자열 값을 원래 모양(배열 등)으로 되돌린다 */
function valuesFromFields(fields, edited) {
  const out = {};
  for (const f of fields || []) {
    const v = edited?.[f.key];
    if (v === undefined || String(v).trim() === '') continue;
    out[f.key] = f.isList ? String(v).split(',').map((s) => s.trim()).filter(Boolean) : v;
  }
  return out;
}

/** 큐에 쌓인 함수호출을 처리한다. 쓰기 작업을 만나면 멈춘다. */
async function processQueue(session, onEvent) {
  while (session.queue.length) {
    const call = session.queue[0];

    // 화면 전용 도구 — 쓰기면 SP 와 똑같이 확인 카드를 띄우고 멈춘다.
    const screenAction = findScreenAction(call.name);
    if (screenAction) {
      if (screenAction.write) {
        const args = call.args || {};
        // summary 는 설명글이라 고치는 값 목록에서 뺀다
        const inputs = Object.keys(args).filter((k) => k !== 'summary').map((k) => ({ key: k, desc: '' }));
        const fields = fieldsFromInputs(inputs, args);
        const build = (values) => {
          const next = { ...values, summary: args.summary };
          return { preview: screenPreviewWithTarget(screenAction, next), exec: () => runScreenAction(screenAction, next), values };
        };
        session.pendingWrite = {
          call,
          preview: screenPreviewWithTarget(screenAction, args),
          summary: args.summary || screenAction.summary || screenAction.description || '',
          fields,
          rebuild: (edited) => build(valuesFromFields(fields, edited)),
          exec: () => runScreenAction(screenAction, args),
        };
        return { paused: true };
      }
      session.queue.shift();
      onEvent?.({ type: 'tool-start', name: call.name, args: call.args });
      const result = await runScreenAction(screenAction, call.args || {});
      onEvent?.({ type: 'tool-end', name: call.name, args: call.args, result });
      session.responses.push(toFunctionResponse(call, result));
      continue;
    }

    // 기능 목록으로 실행 — SP 든 화면 함수든 여기서 한 번에 풀린다
    if (call.name === 'run_capability') {
      const resolved = resolveCapabilityCall(call.args || {});
      if (resolved.error) {
        session.queue.shift();
        session.responses.push(toFunctionResponse(call, { 오류: resolved.error }));
        continue;
      }
      if (resolved.write) {
        const fields = fieldsFromInputs(resolved.cap.inputs, call.args?.values);
        await preloadTargetFor(resolved.preview());
        session.pendingWrite = {
          call,
          preview: resolved.preview(),
          summary: call.args?.summary || resolved.cap.name,
          fields,
          // 값을 고치면 레시피를 처음부터 다시 태운다 (필수값 검사도 다시 걸린다)
          rebuild: (edited) => {
            const again = resolveCapabilityCall({ ...call.args, values: valuesFromFields(fields, edited) });
            if (again.error) return { error: again.error };
            return { preview: again.preview(), exec: again.exec };
          },
          exec: resolved.exec,
        };
        return { paused: true };
      }
      session.queue.shift();
      onEvent?.({ type: 'tool-start', name: call.name, args: call.args });
      const result = await resolved.exec();
      onEvent?.({ type: 'tool-end', name: call.name, args: call.args, result });
      session.responses.push(toFunctionResponse(call, result));
      continue;
    }

    if (call.name === 'run_write') {
      const args = call.args || {};
      const preview = previewProcedureCall(args.procedureName, args.params);
      if (!preview) {
        session.queue.shift();
        session.responses.push(
          toFunctionResponse(call, {
            오류: `'${args.procedureName}' 은(는) 사용할 수 있는 SP 목록에 없습니다. search_procedures 로 정확한 이름을 찾으세요.`,
          })
        );
        continue;
      }
      // 자동으로 채워지는 값(업체번호·처리자)은 고칠 수 없게 뺀다
      const editable = preview.params.filter((p) => !p.auto);
      const fields = editable.map((p) => ({ key: p.name, value: String(p.value ?? ''), desc: '', required: false, isList: false }));
      await preloadTargetFor(preview);
      session.pendingWrite = {
        call,
        preview: attachTarget(preview),
        summary: args.summary || '',
        fields,
        rebuild: (edited) => {
          const params = fields.map((f) => ({ name: f.key, value: edited?.[f.key] ?? f.value }));
          const next = previewProcedureCall(args.procedureName, params);
          if (!next) return { error: `'${args.procedureName}' 을(를) 찾지 못했습니다.` };
          return {
            preview: attachTarget(next),
            exec: async () => {
              try {
                const result = await runProcedure(args.procedureName, params, { allowWrite: true });
                return { 결과: '실행했습니다.', ...result };
              } catch (error) {
                return { 오류: error?.message || String(error) };
              }
            },
          };
        },
      };
      return { paused: true };
    }

    session.queue.shift();
    onEvent?.({ type: 'tool-start', name: call.name, args: call.args });
    const result = await executeReadCall(call);
    onEvent?.({ type: 'tool-end', name: call.name, args: call.args, result });
    session.responses.push(toFunctionResponse(call, result));
  }

  if (session.responses.length) {
    session.contents.push({ role: 'user', parts: session.responses });
    session.responses = [];
  }
  return { paused: false };
}

/** Gemini ↔ SP 왕복 루프 */
async function runLoop(session, { onEvent, signal, route } = {}) {
  const context = buildContext({ route });

  for (let step = 0; step < MAX_STEPS; step += 1) {
    // 재개된 경우: 남은 함수호출부터 처리한다
    if (session.queue.length || session.responses.length) {
      const outcome = await processQueue(session, onEvent);
      if (outcome.paused) return { status: 'confirm', pending: session.pendingWrite };
    }

    const { parts, blocked } = await generate({
      context,
      contents: session.contents,
      tools: buildTools(),
      signal,
    });

    session.contents.push({ role: 'model', parts });

    // 주제 게이트가 막은 응답. 도구 호출이 있을 수 없으므로 여기서 바로 끝낸다.
    if (blocked) {
      return { status: 'done', text: parts.map((p) => p.text || '').join('').trim(), blocked: true };
    }

    const calls = parts.filter((p) => p.functionCall).map((p) => p.functionCall);
    const text = parts
      .filter((p) => typeof p.text === 'string')
      .map((p) => p.text)
      .join('\n')
      .trim();

    if (!calls.length) return { status: 'done', text };

    if (text) onEvent?.({ type: 'thinking', text });
    session.queue = calls;
    const outcome = await processQueue(session, onEvent);
    if (outcome.paused) return { status: 'confirm', pending: session.pendingWrite, text };
  }

  return {
    status: 'done',
    text: '조회를 여러 번 시도했지만 답을 정리하지 못했습니다. 조건을 더 좁혀서 다시 물어봐 주세요.',
  };
}

/** 사용자가 말을 걸었을 때 */
export async function sendMessage(session, text, options = {}) {
  session.contents.push({ role: 'user', parts: [{ text }] });
  return runLoop(session, options);
}

/**
 * 확인 대기 중인 쓰기 작업을 처리한다.
 * @param {boolean} approved  true 면 실제로 SP 를 실행한다.
 * @param {object} [options.values]  확인 창에서 사용자가 고친 값. 주면 그 값으로 다시 조립해 실행한다.
 */
export async function resolveWrite(session, approved, options = {}) {
  const pending = session.pendingWrite;
  if (!pending) return { status: 'done', text: '' };

  const { call } = pending;
  session.pendingWrite = null;
  session.queue.shift(); // 확인 대상이던 호출을 큐에서 뺀다

  let payload;
  if (!approved) {
    payload = { 결과: '사용자가 실행을 취소했습니다. 저장하지 않았습니다.' };
    options.onEvent?.({ type: 'write-declined', name: call.name, args: call.args });
  } else {
    options.onEvent?.({ type: 'tool-start', name: call.name, args: call.args });

    // 사용자가 확인 창에서 값을 고쳤다면 그 값으로 다시 조립한다.
    let edited = null;
    if (options.values && pending.rebuild) {
      const again = pending.rebuild(options.values);
      if (again.error) {
        payload = { 오류: again.error };
        options.onEvent?.({ type: 'tool-end', name: call.name, args: call.args, result: payload });
        session.responses.push(toFunctionResponse(call, payload));
        return runLoop(session, options);
      }
      pending.exec = again.exec;
      pending.preview = again.preview;
      edited = options.values;
    }

    const screenAction = findScreenAction(call.name);
    if (pending.exec) {
      // 기능 목록으로 들어온 것 — 무엇을 어떻게 실행할지는 승인 카드를 만들 때 이미 정해졌다.
      payload = await pending.exec();
      options.onEvent?.({ type: 'tool-end', name: call.name, args: call.args, result: payload });
    } else if (screenAction) {
      payload = await runScreenAction(screenAction, call.args || {});
      options.onEvent?.({ type: 'tool-end', name: call.name, args: call.args, result: payload });
    } else {
      try {
        const result = await runProcedure(call.args?.procedureName, call.args?.params, { allowWrite: true });
        payload = { 결과: '실행했습니다.', ...result };
        options.onEvent?.({ type: 'tool-end', name: call.name, args: call.args, result: payload });
      } catch (error) {
        payload = { 오류: error?.message || String(error) };
        options.onEvent?.({ type: 'tool-end', name: call.name, args: call.args, result: payload });
      }
    }

    // 사용자가 값을 고쳤으면 AI 에게 알린다. 안 그러면 AI 가 자기가 낸 값으로 저장된 줄 알고 답한다.
    if (edited && payload && !payload.오류) {
      payload = { ...payload, 사용자가_값을_수정함: true, 실제_저장한_값: edited };
    }
  }

  session.responses.push(toFunctionResponse(call, payload));
  return runLoop(session, options);
}
